import { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import axios from 'axios';
import { url } from '../Helpers/utils/Constant';
import { debounce } from 'lodash';
import ClearIcon from '@mui/icons-material/Clear';
import { UseFormRegister } from 'react-hook-form';
// Define the Country type
type Country = {
  country_id: number;
  country_name: string;
  created_at?: string;
  status?: number;
  created_by?: null | string;
};

const CountrySelect = ({
  register,
  fieldName,
  label,
  hideCustomLabel,
  isRequired,
  mt,
  onChange,
  defaultValue,
}: {
  register?: UseFormRegister<any>;
  fieldName: string;
  label?: string;
  hideCustomLabel?: boolean;
  isRequired?: boolean;
  mt?: '0';
  onChange?: (e: number) => void;
  defaultValue?: {
    country_id: number;
    country_name: string;
  };
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Fetch country data from the API
  const fetchCountries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<{
        data: Country[];
        message: string;
        success: boolean;
      }>(
        search
          ? `${url}/api/atab/member-company/country?name=${search}`
          : `${url}/api/atab/member-company/country`
      ); // Typed Axios response
      setCountries(data?.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [search]);

  return (
    <Box sx={{ mt: mt ? 0 : 2 }}>
      {!hideCustomLabel && (
        <label>
          {label} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </label>
      )}

      <Autocomplete
        defaultValue={defaultValue || null}
        options={countries}
        getOptionLabel={(option) => option?.country_name}
        onInputChange={debounce((_, value) => {
          setSearch(value);
        }, 300)}
        onChange={(_, value) => {
          register &&
            register(fieldName).onChange({
              target: {
                name: fieldName,
                value: value ? value.country_id : '',
              },
            });
          if (value?.country_id) {
            onChange && onChange(value?.country_id);
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option?.country_id === value?.country_id
        }
        loading={loading}
        clearIcon={
          <span
            onClick={() => {
              setSearch('');
            }}
          >
            <ClearIcon fontSize='small' />
          </span>
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Enter country'
            required={isRequired}
            label={hideCustomLabel ? label : ''}
            variant='outlined'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default CountrySelect;
